import { z } from 'zod';
import { BaseSchema } from '../base/base.entity.model';
import * as FlatModels from '../../internal';

export const AnnotationType = z.enum([
  'Marker',
  'FreeForm',
  'Text',
  'Keymap',
]);
export type AnnotationType = z.infer<typeof AnnotationType>;

export const AnnotationTypeIcon: Record<string, string> = {
  [AnnotationType.enum.Marker]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_MARKER,
  [AnnotationType.enum.Text]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_TEXT,
  [AnnotationType.enum.Keymap]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_KEYMAP,
}

export const TipType = z.enum([
  'dot',
  'arrow',
  'circle',
  'none',
]);
export type TipType = z.infer<typeof TipType>;

export const TipTypeArray = Object.values(TipType.enum).map(value => { return { value: value, name: 'enum.annotation.' + value }; });

export const Shape = z.enum([
  'free',
  'rectangle',
  'ellipse',
  'arrow',
  'interactiveArea',
  'line',
  'triangle',
]);
export type Shape = z.infer<typeof Shape>;

export const ShapeIcon: Record<string, string> = {
  [Shape.enum.free]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_FREE,
  [Shape.enum.interactiveArea]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_INTERACTIVEAREA,
  [Shape.enum.line]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_LINE,
  [Shape.enum.rectangle]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_RECTANGLE,
  [Shape.enum.ellipse]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_ELLIPSE,
  [Shape.enum.arrow]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_ARROW,
  [Shape.enum.triangle]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ANNOTATION_FREEFORM_TRIANGLE,
}

export const SelfTestSchema = z.object({
  ignore: z.boolean().nullable().default(null),
  x: z.number().optional(),
  y: z.number().optional(),
});

export const AnnotationSchema = BaseSchema.extend({
  label: z.string().uuid(),
  source: z.string().uuid(),
  fork: FlatModels.PointSharedModels.PointSchema.optional(),
  groups: z.array(z.string().uuid()).optional(),
  type: AnnotationType,
  selfTest: SelfTestSchema,
  images: z.array(z.string().uuid()).optional(),
  slide: z.string().uuid(),
  link: FlatModels.LinkEntityModels.LinkSchema.optional(),
});

export const MarkerSchema = AnnotationSchema.extend({
  type: z.literal('Marker'),
  targets: z.array(z.string().uuid()).default([]),
});

export type Marker = z.infer<typeof MarkerSchema>;

export const TextSchema = MarkerSchema.extend({
  type: z.literal('Text'),
});

export type Text = z.infer<typeof TextSchema>;

export const FreeFormSchema = AnnotationSchema.extend({
  type: z.literal('FreeForm'),
  shape: Shape,
  path: z.array(FlatModels.PointSharedModels.PointSchema).nullable().default(null),
  freeFormStyle: z.string().uuid(),
  target: z.string().uuid().optional(), // optional; cause some free forms don't have a target id -> error when saving
  linkedSlideId: z.string().optional(),
  linkedSite: z.any().optional(),
});

export type FreeForm = z.infer<typeof FreeFormSchema>;

export const KeymapSortingType = z.enum([
  'horizontal',
  'vertical',
]);
export type KeymapSortingType = z.infer<typeof KeymapSortingType>;

export const KeymapSortingTypeArray = Object.values(KeymapSortingType.enum).map(value => { return { value: value, name: 'enum.annotation.' + value }; });

export const KeymapSchema = AnnotationSchema.extend({
  type: z.literal('Keymap'),
  keymapStyle: z.string().uuid(),
  columns: z.array(z.string().uuid()).default([]),
});

export type Keymap = z.infer<typeof KeymapSchema>;

export const AnnotationEntityTypeSchema = z.discriminatedUnion('type', [
  FreeFormSchema,
  KeymapSchema,
  MarkerSchema,
  TextSchema,
]);

export type AnnotationEntityType = z.infer<typeof AnnotationEntityTypeSchema>;

export const AnnotationEntityQueryResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: AnnotationEntityTypeSchema,
});

export type AnnotationEntityQueryResult = z.infer<typeof AnnotationEntityQueryResultSchema>;

export const PositionAndZoomSchema = z.object({
  currentBounds: z.object({
    x: z.number(),
    y: z.number(),
    height: z.number(),
    width: z.number(),
  }),
  currentPosition: z.object({
    x: z.number(),
    y: z.number(),
    height: z.number(),
    width: z.number(),
    zoom: z.number(),
  }),
  isVM: z.boolean()
});

export type PositionAndZoom = z.infer<typeof PositionAndZoomSchema>;